import { Fab, Grid, SxProps } from '@mui/material';
import React, { Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState } from 'react';
import NotFound from '../../../common/components/NotFound';
import I18n from '../../../common/i18n/I18n';
import CrewImage from '../../../common/icons/IconImages/CrewImage';
import { Icons } from '../../../common/icons/Icons';
import { V } from '../../../common/Layout';
import PersonWorkSpecsModal from '../personWorkSpecifications/PersonWorkSpecsModal';

type SideCardProps = {
    id: string;
    allowedEdit?: boolean;
    title?: string | ReactElement;
    modalTitle: string | ReactElement;
    children: ReactElement[];
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    setIsCreateModalOpen: Dispatch<SetStateAction<boolean>>;
    sx?: SxProps;
    fromMarketplace?: boolean;
    fullWidthContent?: boolean;
    /** Hide the default add Fab when the child renders its own inline toolbar. */
    suppressAddButton?: boolean;
};

export default function SideCard(props: SideCardProps) {
    const {
        id,
        title,
        modalTitle,
        children,
        isModalOpen,
        setIsModalOpen,
        setIsCreateModalOpen,
        sx,
        allowedEdit,
        fromMarketplace = false,
        fullWidthContent = false,
        suppressAddButton = false
    } = {
        ...props
    };

    const gridRef = useRef(null);
    const resizeObserverRef = useRef(null);

    const [showViewAll, setShowViewAll] = useState(null);

    useEffect(() => {
        const divElement = gridRef.current;
        if (!divElement) {
            return;
        }

        resizeObserverRef.current = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const divElement = entry.target;
                setShowViewAll(divElement.scrollHeight > divElement.clientHeight);
            }
        });

        resizeObserverRef.current.observe(divElement);

        return () => {
            if (divElement) {
                resizeObserverRef.current.disconnect(divElement);
            }
        };
    }, [gridRef.current]);

    return (
        <>
            <V
                fill
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    p: 0.75,
                    ...sx
                }}
            >
                {children && children.length ? (
                    <V
                        sx={{
                            overflow: 'auto',
                            ...(fullWidthContent ? { flex: 1 } : {})
                        }}
                    >
                        {allowedEdit && !suppressAddButton && (
                            <Fab
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    width: 32,
                                    height: 32,
                                    minWidth: 32,
                                    minHeight: 32,
                                    alignSelf: 'flex-start',
                                    mb: 2,
                                    boxShadow: 3
                                }}
                                size="small"
                                color="primary"
                                aria-label="add"
                                variant="circular"
                                onClick={() => setIsCreateModalOpen(true)}
                            >
                                <Icons.AddOutlined />
                            </Fab>
                        )}
                        <Grid
                            id={id}
                            container
                            ref={gridRef}
                            spacing={2}
                            sx={{
                                width: '100%',
                                flexGrow: 0,
                                ...(fullWidthContent ? { flex: 1 } : {})
                            }}
                        >
                            {children.map((c, index) => (
                                <Grid key={index} size={{ xs: 12, md: fromMarketplace || fullWidthContent ? 12 : 6 }}>
                                    {c}
                                </Grid>
                            ))}
                        </Grid>
                    </V>
                ) : (
                    <NotFound
                        ImageIcon={<CrewImage />}
                        allowedEdit={allowedEdit}
                        title={<I18n token="no.results" />}
                        sx={{ color: 'text.disabled', gap: 1 }}
                        button
                        buttonText={`person.add.${id}.button`}
                        onClick={() => setIsCreateModalOpen(true)}
                    />
                )}

                {isModalOpen && (
                    <PersonWorkSpecsModal
                        id={id}
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                        title={modalTitle}
                    >
                        {children}
                    </PersonWorkSpecsModal>
                )}
            </V>
        </>
    );
}
